/**
 * Minimal ZIP reader for the course import: lists the entries of a ZIP file
 * and inflates the ones asked for, using the browser's own
 * DecompressionStream('deflate-raw') - no library. Handles stored (0) and
 * deflated (8) entries, ZIP64 included (no encryption).
 *
 * The archive is never loaded as a whole: course exports reach several
 * gigabytes, more than a page may hold in one ArrayBuffer. Only the end
 * record and the central directory are read up front; an entry's bytes are
 * sliced out of the Blob when it is read. A ZIP inside the ZIP is just
 * another Blob, so readZip works on it the same way.
 */

const EOCD_SIGNATURE = 0x06054b50;
const ZIP64_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_EOCD_SIGNATURE = 0x06064b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;
const ZIP64_EXTRA_ID = 0x0001;
const MAX_16 = 0xffff;
const MAX_32 = 0xffffffff;

async function readView(blob, start, end) {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function getUint64(view, offset) {
  return view.getUint32(offset, true) + view.getUint32(offset + 4, true) * 2 ** 32;
}

function corrupt(detail = '') {
  return new Error(`Das ZIP-Archiv ist beschädigt${detail ? ` (${detail})` : ''}.`);
}

/**
 * Where the central directory is and how many entries it has. The end record
 * is at least 22 bytes and may be followed by a comment of up to 64 KiB, so
 * it is searched backwards in the archive's tail.
 */
async function readDirectoryLocation(blob) {
  const tailStart = Math.max(0, blob.size - 22 - MAX_16 - 20);
  const tail = await readView(blob, tailStart, blob.size);
  let eocd = -1;

  for (let offset = tail.byteLength - 22; offset >= 0; offset -= 1) {
    if (tail.getUint32(offset, true) === EOCD_SIGNATURE) {
      eocd = offset;
      break;
    }
  }

  if (eocd < 0) {
    throw new Error('Die Datei ist kein gültiges ZIP-Archiv.');
  }

  let count = tail.getUint16(eocd + 10, true);
  let size = tail.getUint32(eocd + 12, true);
  let offset = tail.getUint32(eocd + 16, true);

  // ZIP64: the classic record holds placeholders, the real values sit in a
  // ZIP64 end record that a locator right before it points to.
  const locator = eocd - 20;
  if (
    (count === MAX_16 || size === MAX_32 || offset === MAX_32)
    && locator >= 0
    && tail.getUint32(locator, true) === ZIP64_LOCATOR_SIGNATURE
  ) {
    const zip64Offset = getUint64(tail, locator + 8);
    const record = await readView(blob, zip64Offset, zip64Offset + 56);

    if (record.getUint32(0, true) !== ZIP64_EOCD_SIGNATURE) {
      throw corrupt('ZIP64');
    }

    count = getUint64(record, 32);
    size = getUint64(record, 40);
    offset = getUint64(record, 48);
  }

  return { count, size, offset };
}

/** Sizes and offset from an entry's ZIP64 extra field, where the header has placeholders. */
function applyZip64Extra(view, start, length, values) {
  const end = start + length;

  for (let at = start; at + 4 <= end;) {
    const id = view.getUint16(at, true);
    const fieldSize = view.getUint16(at + 2, true);
    let field = at + 4;

    if (id === ZIP64_EXTRA_ID) {
      for (const key of ['size', 'compressedSize', 'localOffset']) {
        if (values[key] === MAX_32 && field + 8 <= at + 4 + fieldSize) {
          values[key] = getUint64(view, field);
          field += 8;
        }
      }
      return;
    }

    at += 4 + fieldSize;
  }
}

/**
 * Reads the central directory of a ZIP File or Blob. Returns a Map from path
 * ("a/b/c.png") to an object with `size` and `read()`, which resolves to a
 * Blob of the content.
 */
export async function readZip(blob) {
  const location = await readDirectoryLocation(blob);
  const view = await readView(blob, location.offset, location.offset + location.size);
  const utf8 = new TextDecoder('utf-8');
  const entries = new Map();
  let offset = 0;

  for (let i = 0; i < location.count; i += 1) {
    if (offset + 46 > view.byteLength || view.getUint32(offset, true) !== CENTRAL_SIGNATURE) {
      throw corrupt();
    }

    const method = view.getUint16(offset + 10, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const name = utf8.decode(
      new Uint8Array(view.buffer, offset + 46, nameLength),
    );
    const values = {
      compressedSize: view.getUint32(offset + 20, true),
      size: view.getUint32(offset + 24, true),
      localOffset: view.getUint32(offset + 42, true),
    };

    applyZip64Extra(view, offset + 46 + nameLength, extraLength, values);
    offset += 46 + nameLength + extraLength + commentLength;

    if (name.endsWith('/')) {
      continue;
    }

    const { compressedSize, size, localOffset } = values;

    entries.set(name, {
      size,
      async read() {
        const header = await readView(blob, localOffset, localOffset + 30);

        if (header.getUint32(0, true) !== LOCAL_SIGNATURE) {
          throw corrupt(name);
        }

        const start = localOffset + 30
          + header.getUint16(26, true)
          + header.getUint16(28, true);
        const data = blob.slice(start, start + compressedSize);

        if (method === 0) {
          return data;
        }

        if (method === 8) {
          const stream = data.stream().pipeThrough(new DecompressionStream('deflate-raw'));
          return new Response(stream).blob();
        }

        throw new Error(`Nicht unterstützte ZIP-Kompression in ${name}.`);
      },
    });
  }

  return entries;
}
